import React, { useEffect, useState, useRef } from "react";
import { PaymentRequestButtonElement, useStripe } from "@stripe/react-stripe-js";
import { getIntent, finalizeSimOrder, formatAmountForStripe } from '../services/stripeService';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { secureStorage } from '../Utils/encryption';
import { useTranslation } from 'react-i18next';

export default function WalletButton({ 
  label = "Pay",
  walletType = "google", // 'google' or 'apple'
  totalAmount = 0, 
  cartItems = [],
  selectedCurrency = "USD",
  country = "US",
  onPaymentSuccess,
  onPaymentError,
  validateCartItems,
  affiliateTid = ''
}) {
  const stripe = useStripe();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [esimDetails, setEsimDetails] = useState(null);
  const [isWalletAvailable, setIsWalletAvailable] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: ''
  });
  const billingDetailsRef = useRef({
    name: '',
    email: ''
  });

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '500px',
      width: '90%',
      backgroundColor: 'transparent',
      zIndex: 1001
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    }
  };

  const handleInstallEsim = () => {
    navigate('/activateEsim'); 
  };

  const handleGoHome = () => {
    navigate('/'); 
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage('');
    setEsimDetails(null);
    // Call the checkout success callback to update the checkout page state
    onPaymentSuccess({ id: 'wallet-payment-success', status: 'succeeded' });
    window.location.href = '/';
  };

  const closeFailedModal = () => {
    setShowFailedModal(false);
    setErrorMessage('');
  };

  const isFormValid = () => {
    return (
      billingDetails.name.trim() !== '' &&
      billingDetails.email.trim() !== '' &&
      billingDetails.email.includes('@')
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomButtonClick = () => {
    if (paymentRequest) {
      setShowBillingForm(true);
    }
  };

  const handleBillingFormSubmit = (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      if (!billingDetails.name.trim()) {
        setErrorMessage(t('paymentForm.errors.nameRequired'));
        setShowFailedModal(true);
        return;
      }
      if (!billingDetails.email.trim() || !billingDetails.email.includes('@')) {
        setErrorMessage(t('paymentForm.errors.emailInvalid'));
        setShowFailedModal(true);
        return;
      }
    }
    
    // Store billing details in ref for use in payment processing
    billingDetailsRef.current = {
      name: billingDetails.name,
      email: billingDetails.email
    };
    
    setShowBillingForm(false);
    
    if (paymentRequest) {
      // Proceed with wallet payment
      paymentRequest.show();
    } else {
      // This shouldn't happen since we only render when wallet is available
      setErrorMessage(`${walletType === 'google' ? 'Google' : 'Apple'} Pay is not available. Please use card payment instead.`);
      setShowFailedModal(true);
    }
  };

  const handleBillingFormCancel = () => {
    setShowBillingForm(false);
    setBillingDetails({ name: '', email: '' });
  };

  useEffect(() => {
    // Check if we're in a supported environment for wallet payments
    const checkEnvironment = () => {
      const isUsingHttps = window.location.protocol === 'https:';
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      
      return isUsingHttps || isLocalhost;
    };

    const isSupportedEnvironment = checkEnvironment();
    
    if (!isSupportedEnvironment) {
     
      setLoading(false);
      setIsWalletAvailable(false);
      return;
    }

    if (!stripe) {

      setLoading(false);
      setIsWalletAvailable(false);
      return;
    }

    if (totalAmount <= 0) {
     
      setLoading(false);
      setIsWalletAvailable(false);
      return;
    }

    const initializePaymentRequest = async () => {
      try {
        const amountInCents = formatAmountForStripe(totalAmount);

        const pr = stripe.paymentRequest({
          country: country,
          currency: selectedCurrency.toLowerCase(),
          total: {
            label: walletType === 'google' ? 'Google Pay' : 'Apple Pay',
            amount: amountInCents,
          },
          requestPayerName: true,
          requestPayerEmail: true,
          // Apple Pay only: disable Google Pay so Stripe does not trigger it (Google Pay is handled by pay.js to avoid manifest / "Another PaymentRequest UI" errors).
          ...(walletType === 'apple' && { disableWallets: ['googlePay'] }),
        });

        // Check if the specific wallet is available
        pr.canMakePayment().then(result => {
        
          
          const isAvailable = walletType === 'google' 
            ? result && result.googlePay 
            : result && result.applePay;

          if (isAvailable) {
            setPaymentRequest(pr);
            setIsWalletAvailable(true);
            
            // Listen for payment completion
            pr.on("paymentmethod", async (ev) => {
              // Validate cart items before proceeding with payment
              if (validateCartItems && !validateCartItems()) {
                ev.complete('fail');
                return;
              }

              setIsProcessing(true);
              
              try {

                const orderData = {
                  items: cartItems.map(item => {
                    const baseItem = {
                      type: item.plan_type === 'Data Bundles' ? 'data_bundle' : item.plan_type === 'Global eSIM' ? 'global_esim' : 'unlimited',
                      country: item.country_code || item.iso3 || 'USA',
                      days: item.days || 30,
                      no_of_esims: item.esim_count || 1,
                      data_usage_allowance: item.gb || 20,
                      payment_amount: parseFloat(item.api_calculated_price || item.price) || 0 
                    };

                    if (item.plan_type === 'Unlimited Plans' && item.startDate) {
                      const startDateTimestamp = new Date(item.startDate).getTime().toString();
                      baseItem.start_date = startDateTimestamp;
                    }

                    return baseItem;
                  })
                };

                // For Apple Pay, ensure we have billing details from the form
                const customerName = walletType === 'apple' 
                  ? (billingDetailsRef.current.name || ev.payerName || 'Apple Pay User')
                  : (ev.payerName || 'Google Pay User');
                
                const customerEmail = walletType === 'apple'
                  ? (billingDetailsRef.current.email || ev.payerEmail || '')
                  : (ev.payerEmail || '');

                console.log('Apple Pay Debug:', {
                  walletType,
                  billingDetailsRef: billingDetailsRef.current,
                  customerName,
                  customerEmail,
                  payerName: ev.payerName,
                  payerEmail: ev.payerEmail,
                  billingDetailsState: billingDetails
                });

                // ── FirstPromoter: register lead before payment
                try {
                  if (window.fpr && customerEmail) {
                    window.fpr('track', { type: 'signup', email: customerEmail });
                  }
                } catch (e) { /* never block payment */ }

                const intentData = await getIntent({
                  name: customerName,
                  email: customerEmail,
                  amount: totalAmount,
                  items: orderData.items,
                  currency: selectedCurrency
                });

                if (intentData.status === false) {
                  throw new Error(intentData.message || 'Payment intent creation failed');
                }

                if (!intentData.clientSecret) {
                  throw new Error('Payment intent not created. Please try again.');
                }

                // Confirm payment
                const { error, paymentIntent } = await stripe.confirmPayment({
                  clientSecret: intentData.clientSecret,
                  confirmParams: {
                    payment_method: ev.paymentMethod.id,
                    return_url: `${window.location.origin}/payment-success`,
                  },
                  redirect: 'if_required',
                });

                if (error) {
                  throw error;
                }

                if (paymentIntent.status === 'succeeded') {
                  try {
                    const finalizePayload = {
                      ...orderData,
                      name: customerName,
                      email: customerEmail,
                      payment_intent_id: paymentIntent.id,
                      currency: selectedCurrency,
                      ...(affiliateTid ? { tid: affiliateTid } : {})
                    };


                    const finalizeResponse = await finalizeSimOrder(finalizePayload);
                    const successMessage = finalizeResponse.message || t('paymentForm.success.orderPlaced');
                    
                    setSuccessMessage(successMessage);
                    setEsimDetails(finalizeResponse.data);
                    secureStorage.removeItem('checkoutSession');
                    setShowSuccessModal(true);
                    ev.complete('success');
                  } catch (finalizeError) {
                   
                    let errorMessage = t('paymentForm.errors.orderProcessingFailed');
                    
                    if (finalizeError.message) {
                      errorMessage = finalizeError.message;
                    } else if (finalizeError.response && finalizeError.response.data && finalizeError.response.data.message) {
                      errorMessage = finalizeError.response.data.message;
                    }
                    
                    setErrorMessage(errorMessage);
                    setShowFailedModal(true);
                    onPaymentError(finalizeError);
                    ev.complete('fail');
                  }
                } else {
                  const errorMessage = `Payment status: ${paymentIntent.status}. Please try again.`;
                  setErrorMessage(errorMessage);
                  setShowFailedModal(true);
                  onPaymentError(new Error(errorMessage));
                  ev.complete('fail');
                }
              } catch (error) {
               
                let message = 'Payment failed. Please try again.';
                
                if (error && typeof error === 'object') {
                  if (error.message) {
                    message = error.message;
                  } else if (error.response && error.response.data && error.response.data.message) {
                    message = error.response.data.message;
                  }
                }
                
                setErrorMessage(message);
                setShowFailedModal(true);
                onPaymentError(error);
                ev.complete('fail');
              } finally {
                setIsProcessing(false);
              }
            });
          } else {
          
            setIsWalletAvailable(false);
          }
          setLoading(false);
        }).catch(error => {
          console.error(`${walletType} Pay availability check failed:`, error);
          setIsWalletAvailable(false);
          setLoading(false);
        });

      } catch (error) {
        console.error(`${walletType} Pay initialization error:`, error);
        setIsWalletAvailable(false);
        setLoading(false);
      }
    };

    initializePaymentRequest();

    // Cleanup function
    return () => {
      if (paymentRequest) {
        paymentRequest.off("paymentmethod");
      }
    };
  }, [stripe, totalAmount, selectedCurrency, country, validateCartItems, onPaymentSuccess, onPaymentError, walletType]);

  if (loading) {
    return (
      <div className={`${walletType}-pay-loading`} style={{ 
        padding: '12px', 
        textAlign: 'center', 
        color: '#666',
        fontSize: '14px'
      }}>
        Loading {walletType === 'google' ? 'Google' : 'Apple'} Pay...
      </div>
    );
  }

  const handleButtonClick = () => {
    if (walletType === 'apple') {
      // Show billing form for Apple Pay
      setShowBillingForm(true);
    } else {
      // For Google Pay, proceed directly to wallet payment
      if (paymentRequest) {
        paymentRequest.show();
      }
    }
  };

  // Don't render the button if wallet is not available
  if (!isWalletAvailable) {
    return null;
  }

  return (
    <>
      
      {/* Custom styled button that triggers the payment request */}
      <div 
        className={`custom-${walletType}-pay-button`}
        onClick={handleButtonClick}
        style={{
          width: '200px',
          height: '48px',
          backgroundColor: '#000',
          color: '#fff',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '500',
          fontSize: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s ease',
          border: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      
        }}
        onMouseOver={(e) => {
          e.target.style.opacity = '0.9';
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
        }}
        onMouseOut={(e) => {
          e.target.style.opacity = '1';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }}
      >
        {walletType === 'google' ? (
          <>
            {/* Google Pay Logo */}
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Pay</span>
          </>
        ) : (
          <>
            {/* Apple Pay Logo */}
            <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '5px' }}>
              <path fill="#fff" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <span>Pay</span>
          </>
        )}
      </div>

      {/* Only render PaymentRequestButtonElement when paymentRequest is available */}
      {paymentRequest && (
        <div style={{ display: 'none' }}>
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: "buy",
                  theme: "dark",
                  height: "48px",
                },
              },
            }}
          />
        </div>
      )}

      {/* Payment Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onRequestClose={closeSuccessModal}
        style={customStyles}
        contentLabel="Payment Success"
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" onClick={closeSuccessModal} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="success-img d-flex justify-content-center">
                <img src={window.getPublicUrl('images/check-circle.svg')} className="img-fluid" alt="Success icon" />
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.success.title')}</h3>
                  <p className='mb-0'>{successMessage || t('paymentForm.success.message')}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn view-all get-sim"
                onClick={handleInstallEsim}
              >
                {t('paymentForm.success.howToInstall')}
              </button>
              <button
                type="button"
                className="view-all get-sim Continue"
                onClick={handleGoHome}
              >
                {t('paymentForm.success.goBackHome')}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Payment Failed Modal */}
      <Modal
        isOpen={showFailedModal}
        onRequestClose={closeFailedModal}
        style={customStyles}
        contentLabel="Payment Failed"
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" onClick={closeFailedModal} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.failed.title')}</h3>
                  <p className='mb-0'>{errorMessage || t('paymentForm.failed.message')}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn view-all get-sim"
                onClick={closeFailedModal}
              >
                {t('paymentForm.failed.tryAgain')}
              </button>
              <button
                type="button"
                className="view-all get-sim Continue"
                onClick={handleGoHome}
              >
                {t('paymentForm.failed.goBackHome')}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Billing Information Modal */}
      <Modal
        isOpen={showBillingForm}
        onRequestClose={handleBillingFormCancel}
        style={customStyles}
        portalClassName="billing-modal-portal"
        contentLabel="Billing Information"
      >
        <div className="paymentsuccess">
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" onClick={handleBillingFormCancel} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12">
                  <h3>{t('paymentForm.billingInfo')}</h3>
                  <p className='mb-3'>{t('paymentForm.billingInfoDescription')}</p>
                  
                  <form onSubmit={handleBillingFormSubmit}>
                    <div className="form-group">
                      <label>{t('paymentForm.name')} *</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder={t('paymentForm.namePlaceholder')}
                        value={billingDetails.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentForm.email')} *</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder={t('paymentForm.emailPlaceholder')}
                        value={billingDetails.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="modal-footer border-0 pt-3">
                      <button
                        type="submit"
                        className="view-all get-sim Continue billing-modal-continue-btn"
                        disabled={!isFormValid()}
                      >
                        {t('paymentForm.continueToPayment')}
                      </button>
                      <button
                        type="button"
                        className="btn view-all get-sim billing-modal-cancel-btn"
                        onClick={handleBillingFormCancel}
                      >
                        {t('paymentForm.cancel')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}